import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

interface Props {
  invitationId: string;
  applicantName: string;
  applicantEmail: string;
  alternativeDate: string;
  alternativeTime: string;
  location?: string | null;
  alternativeNotes?: string | null;
  onSuccess?: () => void;
}

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString("de-DE", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

const ConfirmAlternativeDateDialog = ({
  invitationId,
  applicantName,
  applicantEmail,
  alternativeDate,
  alternativeTime,
  location,
  alternativeNotes,
  onSuccess,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(
    "wir freuen uns sehr, Ihnen den von Ihnen vorgeschlagenen Termin zu bestätigen. Bitte kommen Sie pünktlich – wir freuen uns auf das Gespräch."
  );

  const handleSend = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("confirm-alternative-interview", {
        body: { invitationId, customMessage: message },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);

      toast.success("Bestätigungs-E-Mail wurde versendet");
      setOpen(false);
      onSuccess?.();
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "E-Mail konnte nicht gesendet werden");
    } finally {
      setLoading(false);
    }
  };

  const timeShort = String(alternativeTime).slice(0, 5);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white">
          <CheckCircle className="h-4 w-4 mr-2" />
          Alternativtermin bestätigen
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Alternativtermin bestätigen</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-muted/50 p-3 rounded-md text-sm space-y-1">
            <p><strong>An:</strong> {applicantName} ({applicantEmail})</p>
            <p><strong>Datum:</strong> {formatDate(alternativeDate)}</p>
            <p><strong>Uhrzeit:</strong> {timeShort} Uhr</p>
            {location && <p><strong>Ort:</strong> {location}</p>}
            {alternativeNotes && (
              <p className="text-muted-foreground"><strong>Hinweis vom Bewerber:</strong> {alternativeNotes}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="alt-confirm-message">Persönliche Nachricht (optional)</Label>
            <Textarea
              id="alt-confirm-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              placeholder="Ihre Nachricht an den Bewerber..."
            />
            <p className="text-xs text-muted-foreground">
              Diese Nachricht wird zusammen mit der Terminbestätigung in der E-Mail eingefügt.
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
              Abbrechen
            </Button>
            <Button onClick={handleSend} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white">
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Bestätigung senden
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmAlternativeDateDialog;
